const RETRYABLE_COGNITO_ERROR_CODES = new Set(['InternalError', 'InternalErrorException']);

export function getCognitoErrorHttpStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if ('statusCode' in error && typeof error.statusCode === 'number') {
    return error.statusCode;
  }

  if (
    '$metadata' in error &&
    error.$metadata &&
    typeof error.$metadata === 'object' &&
    'httpStatusCode' in error.$metadata &&
    typeof error.$metadata.httpStatusCode === 'number'
  ) {
    return error.$metadata.httpStatusCode;
  }

  if (
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status;
  }

  if ('originalError' in error) {
    return getCognitoErrorHttpStatusCode(error.originalError);
  }

  return undefined;
}

export function getCognitoErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if ('code' in error && typeof error.code === 'string') {
    return error.code;
  }

  if ('name' in error && typeof error.name === 'string') {
    return error.name;
  }

  if ('__type' in error && typeof error.__type === 'string') {
    return error.__type.split('#').pop();
  }

  if ('originalError' in error) {
    return getCognitoErrorCode(error.originalError);
  }

  return undefined;
}

/**
 * A retryable Cognito error is a transient server-side failure (HTTP 500 or an internal Cognito
 * error code) - as opposed to a definitive failure such as wrong credentials or user-not-found.
 * Sign-in round-robin keeps cycling residencies only while such transient errors are seen.
 */
export function isRetryableCognitoError(error: unknown): boolean {
  if (getCognitoErrorHttpStatusCode(error) === 500) {
    return true;
  }

  const errorCode = getCognitoErrorCode(error);
  return errorCode !== undefined && RETRYABLE_COGNITO_ERROR_CODES.has(errorCode);
}
